import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  disconnectSentry,
  fetchSentryConnection,
  fetchSentryOrganizations,
  fetchSentryProjects,
  saveSentrySelection,
} from '../../lib/api/sentry';
import { queryKeys } from './queryKeys';

const invalidateSentry = (queryClient: ReturnType<typeof useQueryClient>, projectId: string) => {
  void queryClient.invalidateQueries({ queryKey: queryKeys.sentry.all(projectId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.search.meta(projectId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.search.all(projectId) });
};

export const useSentryConnection = (token: string | null, projectId: string) =>
  useQuery({
    queryKey: queryKeys.sentry.connection(projectId),
    queryFn: () => fetchSentryConnection(token as string, projectId),
    enabled: Boolean(token) && projectId.length > 0,
  });

export const useSentryOrganizations = (token: string | null, projectId: string, enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.sentry.organizations(projectId),
    queryFn: () => fetchSentryOrganizations(token as string, projectId),
    enabled: enabled && Boolean(token) && projectId.length > 0,
    retry: false,
  });

export const useSentryProjects = (
  token: string | null,
  projectId: string,
  organizationSlug: string,
  enabled: boolean,
) =>
  useQuery({
    queryKey: queryKeys.sentry.projects(projectId, organizationSlug),
    queryFn: () => fetchSentryProjects(token as string, projectId, organizationSlug),
    enabled: enabled && Boolean(token) && projectId.length > 0 && organizationSlug.length > 0,
    retry: false,
  });

export const useSaveSentrySelectionMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveSentrySelection.bind(null, token as string, projectId),
    onSuccess: (connection) => queryClient.setQueryData(queryKeys.sentry.connection(projectId), connection),
    onSettled: () => invalidateSentry(queryClient, projectId),
  });
};

export const useDisconnectSentryMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => disconnectSentry(token as string, projectId),
    onSettled: () => invalidateSentry(queryClient, projectId),
  });
};
